import React from 'react';
import ReactDOM from 'react-dom';
import { signOut } from './auth';
import IpRestrictionAlert from '../components/IpRestrictionAlert/IpRestrictionAlert';

/**
 * Displays a custom alert with IP restriction error message
 * @param {string} clientIp - The client's IP address that was blocked
 */
const showIpRestrictionError = (clientIp) => {
    // Create container for the alert
    const alertContainer = document.createElement('div');
    alertContainer.id = 'ip-restriction-alert-root';
    document.body.appendChild(alertContainer);

    // Render the alert
    ReactDOM.render(
        <IpRestrictionAlert clientIp={clientIp} />,
        alertContainer
    );
};

/**
 * Checks if the error response is an IP restriction error
 * @param {Response} response - The fetch response object
 * @returns {Promise<{isIpRestrictionError: boolean, clientIp: string}>}
 */
const checkIpRestrictionError = async (response) => {
    if (response.status !== 401) {
        return { isIpRestrictionError: false, clientIp: null };
    }

    try {
        // Clone the response so it can still be used by other handlers
        const clonedResponse = response.clone();
        const data = await clonedResponse.json();

        // Check if this is an IP restriction error
        // The backend should return a specific error message or code
        const isIpRestrictionError =
            data?.message === 'IP address not allowed' ||
            data?.message === 'IP address not whitelisted' ||
            (data?.data && data?.data?.client_ip);

        const clientIp = data?.data?.client_ip || data?.client_ip;

        return { isIpRestrictionError, clientIp };
    } catch (error) {
        // If we can't parse the response, it's not an IP restriction error
        return { isIpRestrictionError: false, clientIp: null };
    }
};

/**
 * Handles 401 Unauthorized responses and checks for IP restriction errors
 * If IP restriction error is detected, shows alert and forces logout
 * @param {Response} response - The fetch response object
 * @returns {Promise<Response>} The original response
 */
export const handleIpRestrictionResponse = async (response) => {
    if (response.status === 401) {
        console.log('handleIpRestrictionResponse', response);
        const { isIpRestrictionError, clientIp } = await checkIpRestrictionError(response);

        if (isIpRestrictionError) {
            await signOut();

            // Show the error message
            showIpRestrictionError(clientIp);

            // Throw an error to stop further processing
            throw new Error('IPアドレスは許可されていません');
        }
    }

    return response;
};

/**
 * Middleware for OpenAPI runtime that handles IP restriction errors on 401 responses
 */
export const createIpRestrictionMiddleware = () => ({
    post: async (context) => {
        const { response } = context;
        if (response) {
            await handleIpRestrictionResponse(response);
        }
        return response;
    }
});

